import React, { useRef, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  IconButton
} from '@mui/material';
import 'jspdf-autotable';
import { useCompetencia } from '../../../context/CompetenciaContext';
import QRWithLogo from '../RegistroFrom/QRWithLogo';
import PDFGenerator from './PDFGenerator'; // Asegúrate de que la ruta sea correcta

// Función para formatear la marca de pruebas individuales según su tipo.
const renderMarcaSiembra = (prueba) => {
  if (!prueba) return 'Sin marca';
  const hasValue = Object.values(prueba).some(
    (val) => val !== undefined && val !== null && val !== '' && val !== 0
  );
  if (!hasValue) return 'Sin marca';
  const { tipoMarca } = prueba;
  try {
    switch (tipoMarca) {
      case 'velocidad':
        return `${prueba.marcaSegundos || 0}.${String(prueba.marcaCentesimas || '00').padEnd(2, '0')}`;
      case 'medioFondo':
        return `${prueba.marcaMinutos || 0}:${String(prueba.marcaSegundos || '00').padStart(2, '0')}.${String(prueba.marcaCentesimas || '00').padEnd(2, '0')}`;
      case 'fondo':
        return `${prueba.marcaHoras || 0}:${String(prueba.marcaMinutos || '00').padStart(2, '0')}:${String(prueba.marcaSegundos || '00').padStart(2, '0')}.${String(prueba.marcaCentesimas || '00').padEnd(2, '0')}`;
      case 'campo':
        return `${prueba.marcaMetros || 0}.${String(prueba.marcaCentimetros || '00').padEnd(2, '0')}`;
      case 'combinados':
        return `${prueba.marcaPuntos || 0} puntos`;
      default:
        return 'Sin marca';
    }
  } catch (error) {
    console.error('Error formateando marca:', error);
    return 'Sin marca';
  }
};

// Función para formatear la marca de relevos.
const formatMarcaRelevo = (relevo) => {
  const segundos = (relevo.marcaSegundos || 0).toString();
  const centesimas = (relevo.marcaCentesimas || '00').toString().padEnd(2, '0');
  if (relevo.marcaMinutos || relevo.marcaMinutos === 0) {
    const minutos = relevo.marcaMinutos;
    return `${minutos}:${String(relevo.marcaSegundos || 0).padStart(2, '0')}.${centesimas}`;
  }
  return `${segundos}.${centesimas}`;
};

// Función para renderizar los integrantes y suplentes del relevo.
const renderDatosRelevo = (relevo) => {
  const elementos = [];
  if (relevo.integrantes && relevo.integrantes.some((i) => i.trim() !== '')) {
    relevo.integrantes.forEach((nombre, index) => {
      if (nombre.trim() !== '') {
        elementos.push(
          <Typography key={`int-${index}`} variant="body2">
            <strong>Integrante {index + 1}:</strong> {nombre}
          </Typography>
        );
      }
    });
  }
  if (relevo.suplentes && relevo.suplentes.some((s) => s.trim() !== '')) {
    relevo.suplentes.forEach((nombre, index) => {
      if (nombre.trim() !== '') {
        elementos.push(
          <Typography key={`sup-${index}`} variant="body2">
            {relevo.posicion}
          </Typography>
        );
      }
    });
  }
  return elementos;
};

const TablaInscripciones = ({ datos }) => {
  const { competencias } = useCompetencia();
  const [competenciaActiva, setCompetenciaActiva] = useState({});
  const [pdfDoc, setPdfDoc] = useState(null);
  const qrRef = useRef(null);

  useEffect(() => {
    if (competencias?.length > 0) {
      setCompetenciaActiva(competencias[0]);
    }
  }, [competencias]);

  const handleDownloadPDF = () => {
    if (pdfDoc) {
      pdfDoc.save(`comprobante-${datos?.codigoId || 'inscripcion'}.pdf`);
    }
  };

  // ⬇️⬇️ NUEVO: lista de pruebas con fallback a `pruebas`
  const pruebasList = Array.isArray(datos?.pruebasData) && datos.pruebasData.length
    ? datos.pruebasData
    : Array.isArray(datos?.pruebas)
      ? datos.pruebas
      : []; 
  const categoriaTexto =
    typeof datos?.categoria === 'object' && datos?.categoria
      ? (datos.categoria.nombre || datos.categoria.id)
      : datos?.categoria;

  return (
    <Card
      sx={{
        maxWidth: 800,
        margin: '20px auto',
        backgroundColor: competenciaActiva?.colorPrimario
          ? `${competenciaActiva.colorPrimario}10`
          : '#fff',
        border: `2px solid ${competenciaActiva?.colorPrimario || '#e0e0e0'}`
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={2}
        bgcolor={competenciaActiva?.colorPrimario}
        color="#ffffff"
      >
        <Typography variant="h6">
          {competenciaActiva?.nombre || 'Evento Deportivo'}
        </Typography>
        <IconButton color="inherit">
          <PDFGenerator
            datos={datos}
            onGenerate={setPdfDoc}
            competenciaId={competenciaActiva.id}
          />
        </IconButton>
      </Box>

      <CardContent>
        <Grid container spacing={3}>
          {/* Sección 1: Detalles de la inscripción con QR a la derecha */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: competenciaActiva?.colorPrimario }}
                >
                  Detalles de la inscripción
                </Typography>
                {datos?.codigoId && (
                  <Typography variant="body1">
                    <strong>Registro:</strong> {datos.codigoId}
                  </Typography>
                )}
                <Typography variant="body1">
                  <strong>Nombre:</strong> {datos?.nombre} {datos?.apellido}
                </Typography>
                <Typography variant="body1">
                  <strong>Rama:</strong> {datos?.rama}
                </Typography>
                <Typography variant="body1">
                  <strong>Categoría:</strong> {categoriaTexto}
                </Typography>
                <Typography variant="body1">
                  <strong>Equipo:</strong>{' '}
                  {typeof datos?.equipo === 'object'
                    ? `${datos.equipo.name} (${datos.equipo.teamCode})`
                    : datos?.equipo}
                </Typography>
                <Typography variant="body1">
                  <strong>Fecha de nacimiento:</strong> {datos?.diaNacimiento}/{datos?.mesNacimiento}/{datos?.anioNacimiento}
                </Typography>
                <Typography variant="body1">
                  <strong>Email:</strong> {datos?.email}
                </Typography>
                {datos?.telefono && (
                  <Typography variant="body1">
                    <strong>Teléfono:</strong> {datos?.telefono}
                  </Typography>
                )}
                {datos && competenciaActiva?.precioInscripciones !== undefined && (
                  <Typography variant="body1">
                    <strong>Precio:</strong> ${Number(competenciaActiva?.precioInscripciones).toFixed(2)}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  textAlign="center"
                  p={1}
                  border={`2px dashed ${competenciaActiva?.colorPrimario}`}
                  borderRadius={2}
                >
                  <QRWithLogo
                    value={
                      datos?.codigoId
                        ? `https://pistaycampofotos.photohawk.com/admin/es/photos/${datos.codigoId}`
                        : ''
                    }
                    size={150}
                    logoSrc={competenciaActiva?.logo}
                    logoSize={50}
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    mt={1}
                    sx={{ color: competenciaActiva?.colorPrimario }}
                  >
                    Escanear para verificar
                  </Typography>
                  {datos?.codigoId && (
                    <Typography
                      variant="caption"
                      display="block"
                      mt={1}
                      sx={{ color: competenciaActiva?.colorPrimario }}
                    >
                      Código de registro: {datos.codigoId}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* Sección 2: Pruebas Individuales */}
          {pruebasList.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ color: competenciaActiva?.colorPrimario }} gutterBottom>
                Pruebas Individuales
              </Typography>
              {pruebasList.map((prueba, index) => (
                <Box
                  key={index}
                  mb={2}
                  p={2}
                  bgcolor="#f5f5f5"
                  borderRadius={2}
                >
                  <Typography variant="body1">
                    <strong>Prueba {index + 1}:</strong> {prueba.prueba}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: renderMarcaSiembra(prueba) === 'Sin marca'
                        ? '#666'
                        : competenciaActiva?.colorSecundario
                    }}
                  >
                    <strong>Marca:</strong> {renderMarcaSiembra(prueba)}
                  </Typography>
                </Box>
              ))}
            </Grid>
          )}

          {/* Sección 3: Pruebas de Relevo */}
          {datos?.relevosData?.filter(r => r?.prueba)?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ color: competenciaActiva?.colorPrimario }} gutterBottom>
                Pruebas de Relevo
              </Typography>
              {datos.relevosData.map((relevo, index) => (
                relevo.prueba && (
                  <Box
                    key={index}
                    mb={2}
                    p={2}
                    bgcolor="#f5f5f5"
                    borderRadius={2}
                  >
                    <Typography variant="body1">
                      <strong>Prueba {index + 1}:</strong> {relevo.prueba} {relevo.equipoTipo ? `(${relevo.equipoTipo})` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ color: competenciaActiva?.colorSecundario }}>
                      <strong>Marca:</strong> {formatMarcaRelevo(relevo)}
                    </Typography>
                    <strong>{relevo.posicion}</strong>
                  </Box>
                )
              ))}
            </Grid>
          )}

          {/* Sección 4: Asociación */}
          {datos?.asociacion && (
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ color: competenciaActiva?.colorPrimario }} gutterBottom>
                Asociación
              </Typography>
              <Typography variant="body1">
                <strong>Asociación:</strong> {datos.asociacion}
              </Typography>
            </Grid>
          )}

          {/* Sección 5: Dirección */}
          {(datos?.direccion || datos?.ciudad || datos?.estado || datos?.pais) && (
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ color: competenciaActiva?.colorPrimario }} gutterBottom>
                Dirección
              </Typography>
              {datos.direccion && (
                <Typography variant="body1">
                  <strong>Dirección:</strong> {datos.direccion}
                </Typography>
              )}
              {datos.ciudad && (
                <Typography variant="body1">
                  <strong>Ciudad:</strong> {datos.ciudad}
                </Typography>
              )}
              {datos.estado && (
                <Typography variant="body1">
                  <strong>Estado:</strong> {datos.estado}
                </Typography>
              )}
              {datos.pais && (
                <Typography variant="body1">
                  <strong>País:</strong> {datos.pais}
                </Typography>
              )}
            </Grid>
          )}

          {/* Sección 6: Información del tutor */}
          {datos?.menorEdad && (datos.tutorNombre || datos.tutorEmail || datos.tutorTelefono) && (
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ color: competenciaActiva?.colorPrimario }} gutterBottom>
                Información del tutor
              </Typography>
              {datos.tutorNombre && (
                <Typography variant="body1">
                  <strong>Nombre del tutor:</strong> {datos.tutorNombre}
                </Typography>
              )}
              {datos.tutorEmail && (
                <Typography variant="body1">
                  <strong>Email del tutor:</strong> {datos.tutorEmail}
                </Typography>
              )}
              {datos.tutorTelefono && (
                <Typography variant="body1">
                  <strong>Teléfono del tutor:</strong> {datos.tutorTelefono}
                </Typography>
              )}
            </Grid>
          )}

          {/* Sección 7: Contacto de emergencia */}
          {datos?.contactoEmergencia?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ color: competenciaActiva?.colorPrimario }} gutterBottom>
                Contacto de emergencia
              </Typography>
              {datos.contactoEmergencia.map((contacto, index) => (
                <Box key={index} mb={2}>
                  {contacto.nombreEmergencia && (
                    <Typography variant="body1">
                      <strong>Nombre:</strong> {contacto.nombreEmergencia}
                    </Typography>
                  )}
                  {contacto.telefonoEmergencia && (
                    <Typography variant="body1">
                      <strong>Teléfono:</strong> {contacto.telefonoEmergencia}
                    </Typography>
                  )}
                </Box>
              ))}
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TablaInscripciones;
